<template>
  <div class="px-2">
    <h1 class="text-xs-center">Beacons</h1>

    <v-layout row wrap mb-3>
      <v-flex md3 sm6 pa-2>
        <v-text-field
          v-model="searchKey"
          label="Tag key"
          hide-details
          clearable
          solo
          append-icon="fal fa-search"
          @click:append="refresh(1)"
          @keyup.enter="refresh(1)"
        />
      </v-flex>
      <v-flex md3 sm6 pa-2>
        <st-select
          v-model="selectedStatuses"
          :items="statusOptions"
          label="Status"
          multiple
          clearable
          hide-details
        />
      </v-flex>
      <v-flex md2 sm6 pa-2>
        <v-text-field v-model="protocol" label="Protocol" hide-details clearable />
      </v-flex>
      <v-flex md2 sm6 pa-2>
        <v-text-field v-model="manufacturer" label="Manufacturer" hide-details clearable />
      </v-flex>
      <v-flex md2 sm6 pa-2>
        <v-btn small fab :disabled="apiInProgress" @click="refresh(1)"><v-icon small>fal fa-filter</v-icon></v-btn>
      </v-flex>
    </v-layout>

    <v-data-table
      :headers="headers"
      :items="records"
      :pagination.sync="pagination"
      :total-items="total"
      :loading="apiInProgress"
      :rows-per-page-items="[10, 25, 50]"
      class="elevation-1"
      @update:pagination="onPagination"
    >
      <template v-slot:items="props">
        <tr @click="openDetail(props.item)" style="cursor: pointer">
          <td><code>{{ props.item.id }}</code></td>
          <td>{{ props.item.sourceLabel }}</td>
          <td>
            <router-link
              v-if="props.item.ownerPassengerProfileId"
              :to="{ name: 'passengers-passenger', params: { passengerId: props.item.ownerPassengerProfileId } }"
              @click.native.stop
            >
              {{ props.item.ownerPhoneNumber || 'Passenger' }}
            </router-link>
          </td>
          <td>{{ props.item.statusLabel }}</td>
          <td>{{ props.item.batteryLabel }}</td>
          <td>{{ props.item.barcodeLabel }}</td>
          <td>{{ props.item.unheardRideCount }}</td>
          <td>{{ props.item.replacedLabel }}</td>
        </tr>
      </template>
    </v-data-table>

    <st-dialog v-model="detailOpen" max-width="980" show-close>
      <template v-slot:header>
        <span v-if="detail" class="beacon-dialog-title">
          <code>{{ detail.id }}</code>
          <span v-if="detail.statusLabel" class="info-card__badge">
            <v-icon v-if="detail.statusIcon" small class="mr-1" :color="detail.statusColor">{{ detail.statusIcon }}</v-icon>
            {{ detail.statusLabel }}
          </span>
        </span>
      </template>

      <div v-if="detail" class="beacon-details">
        <div class="beacon-details__pair">
          <div class="info-card">
            <div class="info-card__head">
              <v-icon class="info-card__icon">far fa-signal-stream</v-icon>
              <span class="info-card__label">Tag</span>
            </div>
            <div class="info-card__value"><code>{{ detail.id }}</code></div>
            <v-layout row wrap v-for="row in identityRows" :key="'tag-' + row.label">
              <v-flex sm4 pr-1 class="text-xs-right">
                <strong>{{ row.label }}</strong>
              </v-flex>
              <v-flex sm8 pl-1>
                <router-link
                  v-if="row.passengerId"
                  :to="{ name: 'passengers-passenger', params: { passengerId: row.passengerId } }"
                >{{ row.value }}</router-link>
                <div v-else>{{ row.value }}</div>
              </v-flex>
            </v-layout>
          </div>

          <div class="info-card">
            <div class="info-card__head">
              <v-icon
                v-if="detail.statusIcon"
                class="info-card__icon"
                :color="detail.statusColor"
              >{{ detail.statusIcon }}</v-icon>
              <span class="info-card__label">Health</span>
            </div>
            <v-layout row wrap v-for="row in healthRows" :key="'health-' + row.label">
              <v-flex sm4 pr-1 class="text-xs-right">
                <strong>{{ row.label }}</strong>
              </v-flex>
              <v-flex sm8 pl-1 class="layout row align-center">
                <v-icon v-if="row.icon" small class="mr-1" :color="row.color">{{ row.icon }}</v-icon>
                <div>{{ row.value }}</div>
              </v-flex>
            </v-layout>
          </div>
        </div>

        <div v-if="!isOnlyInvestor || isAdmin" class="beacon-details__pair">
          <div v-if="!isOnlyInvestor" class="info-card">
            <div class="info-card__head">
              <v-icon class="info-card__icon">far fa-sticky-note</v-icon>
              <span class="info-card__label">Note</span>
            </div>
            <v-textarea v-model="note" label="Add note" rows="2" hide-details />
            <v-btn class="mt-2" small :disabled="saving || !note" @click="submitNote">Add note</v-btn>
          </div>
          <div v-if="isAdmin" class="info-card">
            <div class="info-card__head">
              <v-icon class="info-card__icon">far fa-battery-full</v-icon>
              <span class="info-card__label">Battery replaced</span>
            </div>
            <st-date-field
              v-model="replaceAt"
              label="Replaced at"
              hide-details
              with-time
              clearable
              :max="replacementDateMax"
              @focus="refreshReplacementDateMax"
            />
            <div class="grey--text caption">Leave blank to use the current time.</div>
            <div v-if="replacementDateError" class="error--text caption">{{ replacementDateError }}</div>
            <v-text-field v-model="replaceNote" label="Replacement note (optional)" hide-details class="mt-2" />
            <v-btn class="mt-2" small color="primary" :disabled="saving || !!replacementDateError" @click="submitReplacement">Mark battery replaced</v-btn>
          </div>
        </div>

        <div ref="eventsCard" class="info-card">
          <div class="info-card__head">
              <v-icon class="info-card__icon">far fa-list-alt</v-icon>
            <span class="info-card__label">Events</span>
            <v-progress-circular
              v-if="eventsLoading"
              indeterminate
              size="14"
              width="2"
              color="grey"
            />
            <span v-else class="info-card__badge">{{ events.length }}</span>
            <v-btn
              type="button"
              flat
              small
              class="info-card__toggle beacon-events-toggle"
              :aria-expanded="eventsOpen ? 'true' : 'false'"
              @click="eventsOpen = !eventsOpen"
            >
              <v-icon small class="mr-1">{{ eventsOpen ? 'fas fa-chevron-up' : 'fas fa-chevron-down' }}</v-icon>
              {{ eventsOpen ? 'Hide' : 'Show' }}
            </v-btn>
          </div>
          <div v-if="eventsOpen">
            <div v-if="eventsLoading && !events.length" class="info-card__placeholder">Loading history…</div>
            <v-data-table
              v-else
              :headers="eventHeaders"
              :items="events"
              :pagination.sync="eventPagination"
              :rows-per-page-items="eventPageSizes"
              :hide-actions="!showEventPager"
              must-sort
              class="beacon-events-table table--pagination--hide-rows-per-page elevation-0"
            >
              <template v-slot:items="props">
                <tr>
                  <td class="beacon-events-when">{{ props.item.whenLabel }}</td>
                  <td>
                    <div>{{ props.item.title }}</div>
                    <router-link
                      v-if="props.item.passengerProfileId"
                      class="caption"
                      :to="{ name: 'passengers-passenger', params: { passengerId: props.item.passengerProfileId } }"
                    >Passenger</router-link>
                  </td>
                  <td class="beacon-events-source">{{ props.item.source }}</td>
                  <td>
                    <span v-if="props.item.actorLabel">{{ props.item.actorLabel }}</span>
                    <span v-else class="grey--text">—</span>
                  </td>
                  <td class="beacon-events-note">
                    <span v-if="props.item.note">{{ props.item.note }}</span>
                    <span v-else class="grey--text">—</span>
                  </td>
                </tr>
              </template>
              <template slot="no-data">
                <div class="info-card__placeholder">No history yet.</div>
              </template>
            </v-data-table>
          </div>
        </div>
      </div>
    </st-dialog>
  </div>
</template>

<style lang="scss" scoped>
.beacon-dialog-title {
  display: flex;
  align-items: center;
}

.beacon-dialog-title .info-card__badge {
  margin-left: 8px;
}

.beacon-details .info-card {
  height: auto;
  margin-bottom: 8px;
}

.beacon-details__pair {
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  margin-right: -8px;
}

.beacon-details__pair > .info-card {
  flex: 1 1 340px;
  min-width: 0;
  margin-right: 8px;
}

.beacon-details ::v-deep .flex.sm8 {
  min-width: 0;
  word-break: break-word;
}

.beacon-events-toggle {
  margin-left: auto;
}

.beacon-events-when {
  white-space: nowrap;
}

.beacon-events-source {
  text-transform: capitalize;
}

.beacon-events-note {
  word-break: break-word;
}

.beacon-events-table ::v-deep .v-table__overflow {
  max-height: 420px;
  overflow-y: auto;
}

.beacon-events-table ::v-deep thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #fafafa;
}

.beacon-events-table ::v-deep .v-datatable thead th,
.beacon-events-table ::v-deep .v-datatable tbody td {
  height: 32px;
  padding: 2px 8px;
  font-size: 12px;
}

.beacon-events-table ::v-deep .v-datatable__actions {
  min-height: 40px;
}
</style>

<script>
import src from './src'
export default src
</script>
